import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, UserPlus, Mail, Phone, Calendar, MoreVertical, Shield, Ban, Eye, Pencil, Trash2 } from 'lucide-react';
import { DataTable, Column, SortState, Pagination, TableFilters, FilterConfig, FilterValue } from '../../components/DataTable';
import { format } from 'date-fns';

// API helper with cookie auth (admin uses HttpOnly cookie session, not Bearer tokens)
async function fetchApi<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!res.ok) {
    throw new Error(`Request failed: ${res.status}`);
  }
  return res.json();
}

// Matches backend UserDetailDto
interface User {
  id: number;
  cognitoSub: string;
  email: string;
  firstName: string;
  lastName: string;
  profileName: string | null;
  mobileNumber: string | null;
  profilePictureUrl: string | null;
  active: boolean;
  suspended: boolean;
  emailVerified: boolean;
  mobileVerified: boolean;
  roleCode: string;
  createdAt: string;
  lastLoginAt: string | null;
}

// Spring Page<T> response shape
interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  empty: boolean;
}

export function UsersListPage() {
  const navigate = useNavigate();

  // Data state
  const [users, setUsers] = useState<User[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Sort state
  const [sortState, setSortState] = useState<SortState>({ column: 'createdAt', direction: 'desc' });

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterValues, setFilterValues] = useState<FilterValue>({});

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());

  // Action menu state
  const [actionMenuUserId, setActionMenuUserId] = useState<number | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  // Close action menu on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) {
        setActionMenuUserId(null);
      }
    }
    if (actionMenuUserId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [actionMenuUserId]);

  // Filter configuration
  const filters: FilterConfig[] = useMemo(() => [
    {
      id: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'suspended', label: 'Suspended' },
      ],
    },
    {
      id: 'verified',
      label: 'Email Verified',
      type: 'select',
      options: [
        { value: 'true', label: 'Verified' },
        { value: 'false', label: 'Not Verified' },
      ],
    },
    {
      id: 'role',
      label: 'Role',
      type: 'select',
      options: [
        { value: 'TARK-USER', label: 'User' },
        { value: 'TARK-ADMIN', label: 'Admin' },
        { value: 'TARK-MODERATOR', label: 'Moderator' },
      ],
    },
  ], []);

  // Column definitions
  const columns: Column<User>[] = useMemo(() => [
    {
      id: 'user',
      header: 'User',
      accessor: (row) => row.firstName,
      cell: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-sm font-medium">
            {row.firstName?.[0]}{row.lastName?.[0]}
          </div>
          <div>
            <div className="font-medium text-slate-200">
              {row.firstName} {row.lastName}
            </div>
            {row.profileName && (
              <div className="text-xs text-slate-500">@{row.profileName}</div>
            )}
          </div>
        </div>
      ),
      width: '200px',
    },
    {
      id: 'email',
      header: 'Email',
      accessor: 'email',
      cell: (row) => (
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-slate-500" />
          <span>{row.email}</span>
          {row.emailVerified && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-500/20 text-green-400">
              Verified
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'phone',
      header: 'Phone',
      accessor: 'mobileNumber',
      cell: (row) => (
        <div className="flex items-center gap-2 text-slate-400">
          <Phone className="h-4 w-4 text-slate-500" />
          <span>{row.mobileNumber || '\u2014'}</span>
        </div>
      ),
    },
    {
      id: 'role',
      header: 'Role',
      accessor: 'roleCode',
      cell: (row) => {
        const roleDisplay = (row.roleCode || 'TARK-USER').replace('TARK-', '');
        return (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
            row.roleCode === 'TARK-ADMIN'
              ? 'bg-purple-500/20 text-purple-400'
              : row.roleCode === 'TARK-MODERATOR'
              ? 'bg-amber-500/20 text-amber-400'
              : 'bg-slate-500/20 text-slate-400'
          }`}>
            <Shield className="h-3 w-3" />
            {roleDisplay}
          </span>
        );
      },
      width: '120px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'active',
      cell: (row) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
          row.active
            ? 'bg-green-500/20 text-green-400'
            : 'bg-red-500/20 text-red-400'
        }`}>
          {row.active ? 'Active' : 'Suspended'}
        </span>
      ),
      width: '100px',
      align: 'center',
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessor: 'createdAt',
      cell: (row) => (
        <div className="flex items-center gap-2 text-slate-400">
          <Calendar className="h-4 w-4 text-slate-500" />
          <span>{row.createdAt ? format(new Date(row.createdAt), 'MMM d, yyyy') : '\u2014'}</span>
        </div>
      ),
      width: '140px',
    },
    {
      id: 'actions',
      header: '',
      accessor: 'id',
      sortable: false,
      cell: (row) => (
        <div className="relative flex items-center justify-end gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setActionMenuUserId(actionMenuUserId === row.id ? null : row.id);
            }}
            className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-slate-300 transition-colors"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          {actionMenuUserId === row.id && (
            <div
              ref={actionMenuRef}
              className="absolute right-0 top-8 z-50 w-44 bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-1"
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActionMenuUserId(null);
                  navigate(`/users/${row.id}`);
                }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 transition-colors"
              >
                <Eye className="h-4 w-4" />
                View Details
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActionMenuUserId(null);
                  navigate(`/users/${row.id}?edit=true`);
                }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 transition-colors"
              >
                <Pencil className="h-4 w-4" />
                Edit User
              </button>
              <div className="border-t border-slate-700 my-1" />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActionMenuUserId(null);
                  navigate(`/users/${row.id}?tab=danger`);
                }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-400 hover:bg-slate-700 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Delete User
              </button>
            </div>
          )}
        </div>
      ),
      width: '60px',
      align: 'right',
    },
  ], [actionMenuUserId, navigate]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Build query params matching backend: search, suspended, page, size
      const params = new URLSearchParams();
      params.set('page', String(currentPage - 1)); // Backend is 0-indexed
      params.set('size', String(pageSize));

      if (searchQuery) {
        params.set('search', searchQuery);
      }

      // Backend uses 'suspended' param (Boolean), not 'active'
      if (filterValues.status) {
        params.set('suspended', filterValues.status === 'suspended' ? 'true' : 'false');
      }

      const data = await fetchApi<PageResponse<User>>(`/api/v1/admin/users?${params.toString()}`);
      setUsers(data.content || []);
      setTotalItems(data.totalElements || 0);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setUsers([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, filterValues]);

  // Fetch on mount and when params change
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSelectedIds(new Set());
  };

  // Handle page size change
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  // Handle row click
  const handleRowClick = (user: User) => {
    navigate(`/users/${user.id}`);
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Users className="h-7 w-7 text-cyan-400" />
            Users
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Manage user accounts, verification, and permissions
          </p>
        </div>

        <button className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-medium rounded-lg transition-colors">
          <UserPlus className="h-4 w-4" />
          Add User
        </button>
      </div>

      {/* Filters */}
      <TableFilters
        filters={filters}
        values={filterValues}
        onChange={setFilterValues}
        searchValue={searchQuery}
        onSearch={setSearchQuery}
        searchPlaceholder="Search by name, email, or phone..."
      />

      {/* Selection actions */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-4 px-4 py-3 bg-cyan-900/20 border border-cyan-800/50 rounded-lg">
          <span className="text-sm text-slate-300">
            <span className="font-medium text-cyan-400">{selectedIds.size}</span> user(s) selected
          </span>
          <div className="flex items-center gap-2">
            <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 rounded transition-colors">
              <Mail className="h-3.5 w-3.5" />
              Email
            </button>
            <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-900/50 hover:bg-red-900 text-red-400 rounded transition-colors">
              <Ban className="h-3.5 w-3.5" />
              Deactivate
            </button>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="px-4 py-3 bg-red-900/20 border border-red-800/50 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
          <button
            onClick={fetchUsers}
            className="mt-2 text-sm text-red-300 hover:text-red-200 underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Data table */}
      <DataTable
        data={users}
        columns={columns}
        isLoading={isLoading}
        loadingRows={pageSize}
        emptyTitle="No users found"
        emptyDescription="No users match your current filters. Try adjusting your search criteria."
        emptyIcon={<Users className="h-12 w-12" />}
        sortable
        defaultSort={sortState}
        onSort={setSortState}
        manualSorting
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onRowClick={handleRowClick}
        hoverable
      />

      {/* Pagination */}
      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </div>
  );
}
